import React from 'react';
import { Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Body2 } from '@/components/@IntranetLibrary/Typography';
import TitleInfo from '@/components/Approval/RightContainer/ApprovalContent/TitleInfo';
import AuthorAt from '@/components/Approval/RightContainer/ApprovalContent/AuthorAt';
import BookMarkButton from '@/components/Approval/RightContainer/ApprovalContent/BookMarkButton';
import ParticipantsRow from '@/components/Approval/ParticipantsRow';
import { toExternalPersons } from '@/components/Approval/ParticipantsRow/externalParticipants';
import { StyledButton } from '@/components/Approval/Content/DocumentContents/styles';
import {
  EMPTY_SUBJECT,
  getTalkFlowDisplaySubject,
} from '@/components/Record/util/function';
import { modeToTitle } from '@/components/Approval/utils/functions';
import { strDate } from '@/utils/function';
import dayjs from 'dayjs';
import { WorkEventName } from '@/constant/gtm';
import { CustomDivider } from '@/components/Approval/RightContainer/ApprovalContent/styles';

interface DocumentItemProps {
  item: any;
  isDesktop: boolean;
  disabled: boolean;
  onDocumentClick: (id: string) => void;
  onBookmark: (id: string, isBookmarked: boolean) => void;
  workEventTracker: (eventName: string, params: any) => void;
  /** 이 기기에 업로드하지 못한 녹음 백업이 있다 (PORTAL-12583) */
  uploadFailed?: boolean;
}

const DocumentItem: React.FC<DocumentItemProps> = ({
  item,
  isDesktop,
  disabled,
  onDocumentClick,
  onBookmark,
  workEventTracker,
  uploadFailed = false,
}) => {
  const { t } = useTranslation();

  const subject = getTalkFlowDisplaySubject(
    item.subject,
    item.suggestedSubject,
    t(EMPTY_SUBJECT),
  );
  const typeName = modeToTitle(item.documentType);

  // 제목 아래 회의 정보 라인 — 미팅 이름(meetingMsSubject) | 회의 일시(meetingDate).
  // 둘 중 있는 값만 ' | ' 로 이어 붙이고, 둘 다 없으면(예: 일반 문서) 라인을 숨긴다.
  const meetingInfo = [
    item.meetingMsSubject?.trim(),
    item.meetingDate ? dayjs(item.meetingDate).format('YYYY-MM-DD HH:mm') : '',
  ]
    .filter(Boolean)
    .join(' | ');
  const hasParticipants =
    toExternalPersons(item.participants.external).length +
      item.participants.internal.length >
    0;
  const showParticipantsSection = isDesktop || hasParticipants;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    workEventTracker(WorkEventName.viewWork, {
      area_name: 'Document 목록',
      post_title: subject,
      post_category: typeName,
      creator: `${item.createdBy.name} ${item.createdBy.positionName}`,
      contents_id: item.id,
    });

    if (e.ctrlKey || e.metaKey) return;

    e.preventDefault();
    e.stopPropagation();
    onDocumentClick(item.id);
  };

  const handleBookmarkClick = () => {
    onBookmark(item.id, item.isBookmarked);
    workEventTracker(WorkEventName.clickPostAction, {
      area_name: 'Document 목록',
      post_title: subject,
      action_type: '북마크',
      post_category: typeName,
      contents: subject,
      contents_id: item.id,
      status: !item.isBookmarked,
    });
  };

  return (
    <StyledButton
      rel="noopener noreferrer"
      target="_blank"
      href={`${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${item.id}`}
      onClick={handleClick}
    >
      <Stack
        direction="row"
        gap={'16px'}
        alignItems="center"
        sx={{
          width: '100%',
          padding: '12px 8px',
          backgroundColor: '#fff',
          '&:hover': { backgroundColor: '#f9fafb' },
        }}
      >
        <TitleInfo
          subject={subject}
          metaLine={meetingInfo || undefined}
          type={{
            tag: item.documentType,
            name: t(typeName),
            isLoading: item.documentSummarizedStatus === 'progressSummarized',
          }}
          commentCount={item.commentCount}
          isRead={item.isRead}
          failLabels={uploadFailed ? [t('업로드 실패')] : []}
        />

        {showParticipantsSection && (
          <Stack
            gap="4px"
            sx={isDesktop ? { minWidth: 196, maxWidth: 196 } : {}}
          >
            <ParticipantsRow participants={item.participants} />
            {isDesktop && (
              <Box className="ellipsis">
                <Body2 component="span" sx={{ color: 'var(--dk-text, #000)', fontWeight: 500 }}>
                  {item.createdBy.name}
                </Body2>
                <Body2 component="span" sx={{ paddingLeft: '4px' }}>
                  {item.createdBy.divisionName} {item.createdBy.departmentName}
                </Body2>
              </Box>
            )}
          </Stack>
        )}

        <AuthorAt authoredAt={strDate(t, item.createdAt)} />

        <BookMarkButton
          isBookMark={item.isBookmarked}
          handleClick={handleBookmarkClick}
          disabled={disabled}
        />
      </Stack>
      <CustomDivider />
    </StyledButton>
  );
};

export default DocumentItem;
